// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.

import React, { useEffect } from 'react';
import {
  DefaultButton,
  DetailsList,
  DetailsListLayoutMode,
  IColumn,
  IModalStyles,
  ITextStyles,
  Modal,
  PrimaryButton,
  Stack,
  Text
} from '@fluentui/react';
import {
  CallIdHistory,
  NUMBER_OF_CALL_IDS_TO_STORE,
  clearCallIdHistory,
  getCallIdHistoryFromLocalStorage
} from 'utils/callIdTracking';

export const CallIdHistoryModal = (props: { isOpen: boolean; onDismiss: () => void }): JSX.Element => {
  const [tableItems, setTableItems] = React.useState<CallIdHistory>([]);

  // Update table items when the modal is opened
  useEffect(() => {
    if (props.isOpen) {
      setTableItems(
        getCallIdHistoryFromLocalStorage().map((item) => ({ ...item, date: new Date(item.date).toLocaleString() }))
      );
    }
  }, [props.isOpen]);

  return (
    <Modal isOpen={props.isOpen} styles={modalStyles} onDismiss={props.onDismiss}>
      <Stack tokens={{ childrenGap: '1rem' }}>
        <Stack.Item align="start">
          <Text styles={headingTestStyles}>Call Id History</Text>
        </Stack.Item>
        {tableItems.length > 0 && (
          <Stack.Item styles={{ root: { margin: '1rem 0.1rem 0rem' } }}>
            <Text>List of previous Call Ids. Only the last {NUMBER_OF_CALL_IDS_TO_STORE} are tracked.</Text>
          </Stack.Item>
        )}
        <Stack.Item>
          {tableItems.length === 0 ? (
            <Text>No Call Ids found in localStorage.</Text>
          ) : (
            <CallIdsHistoryTable callIdHistory={tableItems} />
          )}
        </Stack.Item>
        <Stack.Item>
          <Stack horizontal tokens={{ childrenGap: '1rem' }} horizontalAlign="end">
            <DefaultButton
              disabled={tableItems.length === 0}
              onClick={() => {
                clearCallIdHistory();
                setTableItems([]);
              }}
            >
              Clear History
            </DefaultButton>
            <PrimaryButton onClick={props.onDismiss}>Close</PrimaryButton>
          </Stack>
        </Stack.Item>
      </Stack>
    </Modal>
  );
};

const CallIdsHistoryTable = (props: { callIdHistory: CallIdHistory }): JSX.Element => {
  return (
    <DetailsList
      items={props.callIdHistory}
      compact={false}
      columns={callIdHistoryTableColumns}
      layoutMode={DetailsListLayoutMode.fixedColumns}
      isHeaderVisible={true}
      selectionMode={0}
    />
  );
};

const callIdHistoryTableColumns: IColumn[] = [
  {
    key: 'Date',
    name: 'Date',
    fieldName: 'date',
    minWidth: 180,
    isRowHeader: true,
    isResizable: true,
    isSorted: true,
    isSortedDescending: true,
    data: 'string',
    isPadded: false
  },
  {
    key: 'callId',
    name: 'Call Id',
    fieldName: 'callId',
    minWidth: 300,
    isRowHeader: true,
    isResizable: true,
    isSortedDescending: false,
    data: 'string',
    isPadded: false
  }
];

const modalStyles: Partial<IModalStyles> = {
  main: {
    padding: '2rem'
  }
};

const headingTestStyles: ITextStyles = {
  root: {
    fontWeight: '600',
    fontSize: '1.5rem'
  }
};
